<template>
  <div class="budget-lider-detalhes">
    <div class="d-flex align-items-center mb-1">
      <b-button size="sm" variant="flat-secondary" @click="$router.push({ name: 'budget-lider' })">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />Voltar
      </b-button>
      <h5 class="mb-0 ml-1">Detalhes do Budget do Líder</h5>
    </div>

    <div v-if="isLoading" class="text-center py-5"><b-spinner /></div>
    <template v-else-if="budget">
      <b-row>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Valor Total</small>
            <h3 class="mb-0">{{ formatarMoeda(budget.valor) }}</h3>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Gasto</small>
            <h3 class="mb-0 text-warning">{{ formatarMoeda(budget.valorgasto) }}</h3>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <small class="text-muted d-block">Saldo</small>
            <h3 class="mb-0" :class="saldo <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(saldo) }}
            </h3>
          </b-card>
        </b-col>
      </b-row>

      <b-card title="Informações">
        <b-row>
          <b-col md="6"><strong>Supervisor:</strong> {{ budget.supervisor_nome || budget.supervisorid }}</b-col>
          <b-col md="6"><strong>Líder:</strong> {{ budget.lider_nome || budget.liderid || '-' }}</b-col>
          <b-col md="6"><strong>Situação:</strong>
            <b-badge :variant="budget.ativo ? 'light-success' : 'light-secondary'">
              {{ budget.ativo ? 'Ativo' : 'Inativo' }}
            </b-badge>
          </b-col>
          <b-col md="6"><strong>Início:</strong> {{ formatarData(budget.datainicio) }}</b-col>
          <b-col md="6"><strong>Término:</strong> {{ formatarData(budget.datatermino) }}</b-col>
          <b-col v-if="budget.descricao" md="12" class="mt-50"><strong>Descrição:</strong> {{ budget.descricao }}</b-col>
        </b-row>
      </b-card>

      <b-card title="Earmarcação da verba">
        <div v-if="!budget.alocacoes || !budget.alocacoes.length" class="text-muted">
          Sem earmarcações específicas — toda a verba é gerida no pool.
        </div>
        <b-table
          v-else
          :items="budget.alocacoes"
          :fields="alocFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(tipo_alvo)="data">
            <b-badge :variant="data.item.tipo_alvo === 'cliente' ? 'light-info' : 'light-primary'">
              {{ data.item.tipo_alvo === 'cliente' ? 'Cliente' : 'Representante' }}
            </b-badge>
          </template>
          <template #cell(alvo_nome)="data">
            {{ data.item.alvo_nome || data.item.alvo_id }}
          </template>
          <template #cell(valor)="data">{{ formatarMoeda(data.item.valor) }}</template>
          <template #cell(valorgasto)="data">{{ formatarMoeda(data.item.valorgasto) }}</template>
          <template #cell(saldo)="data">
            <span :class="Number(data.item.saldo) <= 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.saldo) }}
            </span>
          </template>
        </b-table>
      </b-card>

      <b-card title="Histórico de movimentações">
        <div v-if="!budget.logs || !budget.logs.length" class="text-muted">Nenhuma movimentação registrada.</div>
        <b-table
          v-else
          :items="budget.logs"
          :fields="logFields"
          small
          responsive
          class="mb-0"
        >
          <template #cell(datahora)="data">{{ formatarDataHora(data.item.datahora) }}</template>
          <template #cell(usuario_nome)="data">
            <span :title="data.item.usuario_id ? `ID ${data.item.usuario_id}` : ''">
              {{ data.item.usuario_nome || (data.item.usuario_id ? `Usuário ${data.item.usuario_id}` : '—') }}
            </span>
          </template>
          <template #cell(alvo)="data">
            <template v-if="data.item.tipo_alvo">
              <b-badge :variant="data.item.tipo_alvo === 'cliente' ? 'light-info' : 'light-primary'">
                {{ data.item.tipo_alvo === 'cliente' ? 'Cliente' : 'Rep.' }}
              </b-badge>
              <span class="ml-25">{{ data.item.alvo_id || '' }}</span>
            </template>
            <span v-else>-</span>
          </template>
          <template #cell(saldo_antes)="data">{{ formatarSaldo(data.item.saldo_antes) }}</template>
          <template #cell(saldo_depois)="data">{{ formatarSaldo(data.item.saldo_depois) }}</template>
          <template #cell(valor)="data">
            <span :class="Number(data.item.valor) < 0 ? 'text-danger' : 'text-success'">
              {{ formatarMoeda(data.item.valor) }}
            </span>
          </template>
        </b-table>
      </b-card>
    </template>
    <b-card v-else>
      <div class="text-center text-muted py-4">Budget do líder não encontrado.</div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BSpinner, BTable, BBadge,
} from 'bootstrap-vue';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'DetalhesBudgetLider',
  components: {
    BCard, BRow, BCol, BButton, BSpinner, BTable, BBadge,
  },
  data() {
    return {
      alocFields: [
        { key: 'tipo_alvo', label: 'Tipo', class: 'text-center' },
        { key: 'alvo_nome', label: 'Alvo' },
        { key: 'valor', label: 'Alocado', class: 'text-right' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      logFields: [
        { key: 'datahora', label: 'Data/Hora' },
        { key: 'usuario_nome', label: 'Quem fez' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'alvo', label: 'Alvo' },
        { key: 'saldo_antes', label: 'Saldo antes', class: 'text-right' },
        { key: 'saldo_depois', label: 'Saldo depois', class: 'text-right' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapState('BudgetLider', { budget: 'budgetAtual', isLoading: 'isLoading' }),
    saldo() {
      if (!this.budget) return 0;
      return Number(this.budget.valor || 0) - Number(this.budget.valorgasto || 0);
    },
  },
  mounted() {
    this.buscarPorId(this.$route.params.id);
  },
  methods: {
    ...mapActions('BudgetLider', ['buscarPorId']),
    formatarMoeda(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    // saldo antes/depois pode ser null (logs antigos) → exibe travessão.
    formatarSaldo(v) {
      if (v === null || v === undefined || v === '') return '—';
      return this.formatarMoeda(v);
    },
    formatarData(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
    formatarDataHora(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleString('pt-BR');
    },
  },
};
</script>
